import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { usePropertyField, useContentToken, useSlotGroup } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { PetsAllowedIcon } from '@vibesites/assets';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

function hasValue(val: unknown): val is string {
  if (val === null || val === undefined) return false;
  const s = String(val).trim().toLowerCase();
  return (
    s !== '' &&
    s !== 'false' &&
    s !== 'null' &&
    s !== 'undefined' &&
    s !== '0' &&
    s !== 'none' &&
    s !== 'n/a' &&
    s !== 'na'
  );
}

function isTruthy(val: unknown): boolean {
  if (val === true) return true;
  if (!val) return false;
  const s = String(val).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
}

function formatCurrency(val: string): string {
  const num = parseFloat(val.replace(/[^0-9.]/g, ''));
  if (isNaN(num)) return val;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function PetPolicy() {
  const allowsCats = usePropertyField('property_allows_cats');
  const allowsDogs = usePropertyField('property_allows_dogs');
  const restrictions = usePropertyField('property_pet_restrictions') as string | null;
  const policyDescription = usePropertyField('property_pet_policy_description') as string | null;
  const policyInfo = usePropertyField('property_petpolicy_info') as string | null;

  const ctaLink = useSlotGroup('petpolicy.cta_link', ctaLinkDef);

  const petDepositSublabel = useContentToken('petpolicy.pet_deposit_sublabel');
  const petFeeSublabel = useContentToken('petpolicy.pet_fee_sublabel');

  // Stat values — defaultField in the schema pulls the property value; authors can override per-site.
  const maxPetsValue = useContentToken('petpolicy.max_pets_value');
  const maxWeightValue = useContentToken('petpolicy.max_weight_value');
  const petDepositValue = useContentToken('petpolicy.pet_deposit_value');
  const petFeeValue = useContentToken('petpolicy.pet_fee_value');
  const monthlyRentValue = useContentToken('petpolicy.monthly_rent_value');

  const catsDogsList = [
    isTruthy(allowsDogs) && 'Dogs',
    isTruthy(allowsCats) && 'Cats',
  ].filter(Boolean);
  const computedWelcomeText =
    catsDogsList.length > 0 ? `${catsDogsList.join(' & ')} Welcome` : 'Dogs & Cats Welcome';

  // Editable welcome text — authors can override it per-site.
  const welcomeText = useContentToken('petpolicy.welcome_text');

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  // Show the welcome badge if either pet type is allowed by the property
  // OR if an author has explicitly set a custom welcome text.
  const showWelcomeBadge = catsDogsList.length > 0 || Boolean(welcomeText);

  const showMaxPets = hasValue(maxPetsValue);
  const showMaxWeight = hasValue(maxWeightValue);
  const showPetDeposit = hasValue(petDepositValue);
  const showPetFee = hasValue(petFeeValue);
  const showMonthlyRent = hasValue(monthlyRentValue);
  const showStats =
    showMaxPets || showMaxWeight || showPetDeposit || showPetFee || showMonthlyRent;

  const descriptionHtml = policyDescription || policyInfo || null;

  return (
    <section ref={sectionRef} className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pet-layout">
          {/* Left column: description + restrictions */}
          <div className="pet-content">
            {/* Pets welcome badge */}
            {showWelcomeBadge && (
              <div className="flex items-center gap-3 mb-6">
                <PetsAllowedIcon size={28} color="var(--vibe-color-primary)" />
                <RichTextContent
                  path="petpolicy.welcome_text"
                  as="span"
                  className="font-heading text-lg text-black font-medium tracking-wide uppercase"
                />
              </div>
            )}

            {/* Policy description */}
            {descriptionHtml && (
              <RichTextContent
                path="petpolicy.description"
                label="Pet Policy Description"
                defaultField="property_pet_policy_description"
                as="div"
                className="font-body text-[0.95rem] leading-relaxed text-[var(--vibe-color-text)] mb-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
              />
            )}

            {/* Pet restrictions */}
            {hasValue(restrictions) && (
              <div className="pet-restrictions">
                <RichTextContent
                  path="petpolicy.restrictions_heading"
                  as="h3"
                  className="font-heading text-base font-medium text-[var(--vibe-color-text)] mb-3 tracking-wide uppercase"
                />
                <RichTextContent
                  path="petpolicy.restrictions"
                  label="Pet Restrictions"
                  defaultField="property_pet_restrictions"
                  as="div"
                  className="font-body text-sm leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:mb-0.5 [&_a]:text-primary [&_a]:underline"
                />
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              <EditableSlotGroup groupPath="petpolicy.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/amenities'}
                  target={ctaLink._target || '_self'}
                  className="pet-cta"
                >
                  {ctaLink.label || 'View Amenities'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Right column: stat cards */}
          {showStats && (
            <div className="pet-stats">
              {showMaxPets && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_pets_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_pets_value" as="span" className="pet-stat-value">
                    {maxPetsValue ? `${maxPetsValue} Allowed` : ''}
                  </EditableToken>
                </div>
              )}
              {showMaxWeight && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.max_weight_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.max_weight_value" as="span" className="pet-stat-value">
                    {maxWeightValue ? `${maxWeightValue} lbs` : ''}
                  </EditableToken>
                </div>
              )}
              {showPetDeposit && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_deposit_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_deposit_value" as="span" className="pet-stat-value">
                    {petDepositValue ? formatCurrency(petDepositValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_deposit_sublabel" as="span" className="pet-stat-sublabel">
                    {petDepositSublabel || '(Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showPetFee && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.pet_fee_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.pet_fee_value" as="span" className="pet-stat-value">
                    {petFeeValue ? formatCurrency(petFeeValue) : ''}
                  </EditableToken>
                  <EditableToken path="petpolicy.pet_fee_sublabel" as="span" className="pet-stat-sublabel">
                    {petFeeSublabel || '(Non-Refundable)'}
                  </EditableToken>
                </div>
              )}
              {showMonthlyRent && (
                <div className="pet-stat-card">
                  <RichTextContent path="petpolicy.monthly_rent_label" as="span" className="pet-stat-label" />
                  <EditableToken path="petpolicy.monthly_rent_value" as="span" className="pet-stat-value">
                    {monthlyRentValue ? `${formatCurrency(monthlyRentValue)}/mo` : ''}
                  </EditableToken>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .pet-layout {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
        }
        @media (min-width: 768px) {
          .pet-layout {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
            align-items: start;
          }
        }

        .pet-content {
          order: 2;
        }
        @media (min-width: 768px) {
          .pet-content {
            order: 1;
          }
        }

        .pet-stats {
          order: 1;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }
        @media (min-width: 768px) {
          .pet-stats {
            order: 2;
          }
        }

        .pet-stat-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 1.25rem 1rem;
          border: 1px solid #e8e8ea;
          background: #f7f7f8;
          border-radius: 4px;
          min-height: 100px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
        }

        .pet-stat-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-text);
          margin-bottom: 0.5rem;
        }

        .pet-stat-value {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
        }

        .pet-stat-sublabel {
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          color: var(--vibe-color-text-muted);
          opacity: 0.7;
          margin-top: 0.15rem;
        }

        .pet-restrictions {
          padding: 1rem 1.25rem;
          background: #f7f7f8;
          border-left: 3px solid var(--vibe-color-primary);
          margin-bottom: 0.5rem;
        }

        .pet-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.85rem 2rem;
          font-family: var(--vibe-font-heading, var(--vibe-font-body, sans-serif));
          font-size: 0.8rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #fff);
          background-color: var(--vibe-color-primary);
          text-decoration: none;
          transition: opacity 0.25s ease;
        }
        .pet-cta:hover {
          opacity: 0.85;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.welcome_text': {
      label: 'Pets Welcome Text',
      type: 'richtext',
      description: 'Badge text shown next to the paws icon.',
      defaultValue: '<p>Dogs &amp; Cats Welcome</p>',
    },
    'petpolicy.description': {
      label: 'Pet Policy Description',
      type: 'richtext',
      defaultField: 'property_pet_policy_description',
      suggestedFields: ['property_pet_policy_description', 'property_petpolicy_info'],
    },
    'petpolicy.restrictions_heading': {
      label: 'Pet Restrictions Heading',
      type: 'richtext',
      description: 'Heading shown above the pet restrictions list.',
      defaultValue: '<p>Pet Restrictions</p>',
    },
    'petpolicy.restrictions': {
      label: 'Pet Restrictions',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    'petpolicy.max_pets_label': {
      label: 'Pets Per Household Label',
      type: 'richtext',
      defaultValue: '<p>Pets Per Household</p>',
    },
    'petpolicy.max_pets_value': {
      label: 'Pets Per Household Value',
      type: 'text',
      defaultField: 'property_max_pets',
      description:
        'Number of pets allowed per household. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.max_weight_label': {
      label: 'Max Weight Label',
      type: 'richtext',
      defaultValue: '<p>Max Weight</p>',
    },
    'petpolicy.max_weight_value': {
      label: 'Max Weight Value',
      type: 'text',
      defaultField: 'property_max_pet_weight_limit',
      description:
        'Maximum pet weight in lbs. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_label': {
      label: 'Pet Deposit Label',
      type: 'richtext',
      defaultValue: '<p>Pet Deposit</p>',
    },
    'petpolicy.pet_deposit_value': {
      label: 'Pet Deposit Value',
      type: 'text',
      defaultField: 'property_pet_deposit',
      description:
        'Pet deposit amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_deposit_sublabel': {
      label: 'Pet Deposit Sublabel',
      type: 'text',
      defaultValue: '(Refundable)',
    },
    'petpolicy.pet_fee_label': {
      label: 'Pet Fee Label',
      type: 'richtext',
      defaultValue: '<p>Pet Fee</p>',
    },
    'petpolicy.pet_fee_value': {
      label: 'Pet Fee Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      description:
        'Upfront pet fee amount. Pulled from the property automatically; override here to display a custom value.',
    },
    'petpolicy.pet_fee_sublabel': {
      label: 'Pet Fee Sublabel',
      type: 'text',
      defaultValue: '(Non-Refundable)',
    },
    'petpolicy.monthly_rent_label': {
      label: 'Monthly Pet Rent Label',
      type: 'richtext',
      defaultValue: '<p>Monthly Pet Rent</p>',
    },
    'petpolicy.monthly_rent_value': {
      label: 'Monthly Pet Rent Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      description:
        'Monthly pet rent amount. Pulled from the property automatically; override here to display a custom value.',
    },
  },
  slotGroups: {
    'petpolicy.cta_link': ctaLinkDef,
  },
};

(PetPolicy as any).contentSchema = contentSchema;

export default PetPolicy;
